import * as React from "react";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { useLocation } from "react-router-dom";
import { UIControlsText } from "../../content/UIControlsText";
import { isNullOrEmpty } from "./common/util";
import { IError } from "../../models/IError";
import {
    getRuleValidationMessage,
    getEntityDataFromSession,
} from "./common/util";
import { MessageTypeText } from "../../models/MessageTypeText";
import { SessionStorageConsts } from "../../models/SessionStorageConsts";

const sessionResourceData = getEntityDataFromSession<any>(
    SessionStorageConsts.resourceStrings,
);

export const ErrorPage: React.FunctionComponent<IError> = (props: IError) => {
    const location = useLocation();
    const historyProps: any = location.state;

    // Read URL query parameters for reason-based error messages
    const queryParams = new URLSearchParams(location.search);
    const reason = queryParams.get("reason");
    const email = queryParams.get("email");
    const isNotRegistered = reason === "not_registered";

    // Clear session and redirect to logout/login
    const backToLogin = (): void => {
        sessionStorage.clear();
        window.open(process.env.REACT_APP_RSLR_LOGOUT_API_URI, "_self");
    };

    const showHideBackToLogin = () => {
        if (isNullOrEmpty(props.showBackToLogin)) {
            return true;
        } else {
            return props.showBackToLogin;
        }
    };

    const showSomethingWrongMessage = () => {
        if (isNullOrEmpty(props.showSomethingWrongMessage)) {
            return true;
        } else {
            return props.showSomethingWrongMessage;
        }
    };

    // Friendly main heading (from resources if available)
    const friendlyHeading = getRuleValidationMessage(
        sessionResourceData,
        MessageTypeText.OpportunityValidationError,
        "SomeThingWrong",
        UIControlsText.SomeThingWrong,
    );

    // Primary user-facing message (if custom provided use that, otherwise use a generic)
    const primaryMessage =
        props?.customMessage ||
        historyProps?.message ||
        "An unexpected error occurred.";
    const shouldRenderHtmlMessage = historyProps?.isHtmlMessage === true;

    // Technical details (for support) - keep small and monospaced; consider not showing secrets here
    const technicalDetails =
        historyProps?.suppressTechnicalDetails
            ? ""
            : historyProps?.message || props?.technicalMessage || "";

    // Inline styles kept local so no external CSS changes are required
    const styles = {
        wrapper: {
            display: "flex",
            justifyContent: "center",
            padding: "32px 16px",
        } as React.CSSProperties,
        card: {
            maxWidth: 840,
            width: "100%",
            background: "#fff",
            borderRadius: 10,
            padding: "24px 28px",
            boxShadow: "0 8px 24px rgba(16,24,40,0.06)",
        } as React.CSSProperties,
        heading: {
            margin: 0,
            fontSize: "1.6rem",
            fontWeight: 700,
            color: "#0f172a",
        } as React.CSSProperties,
        subHeading: {
            margin: "8px 0 18px 0",
            fontSize: "1rem",
            color: "#374151",
        } as React.CSSProperties,
        techBlock: {
            background: "#f8fafc",
            borderRadius: 6,
            padding: "12px 14px",
            fontFamily: "SFMono-Regular, Menlo, Monaco, 'Courier New', monospace",
            fontSize: "0.85rem",
            color: "#0f172a",
            overflowX: "auto",
            whiteSpace: "pre-wrap",
            lineHeight: 1.4,
            marginBottom: 16,
        } as React.CSSProperties,
        buttonRow: {
            display: "flex",
            gap: 12,
            alignItems: "center",
        } as React.CSSProperties,
    };

    return (
        <div style={{ textAlign: "center", marginTop: 24 }}>
            <div style={styles.wrapper}>
                <div style={styles.card} role="alert" aria-live="polite">
                    {isNotRegistered ? (
                        <>
                            <h2 style={styles.heading}>Account Not Registered</h2>
                            <div style={{ marginTop: 8 }}>
                                <h3 style={styles.subHeading}>
                                    Your account is not registered in our system.
                                </h3>
                                <p style={{ fontSize: "0.95rem", color: "#374151" }}>
                                    Please contact your administrator to request access.
                                </p>
                            </div>
                        </>
                    ) : (
                        <>
                            {showSomethingWrongMessage() && (
                                <div>
                                    <h2 style={styles.heading}>{friendlyHeading}</h2>
                                </div>
                            )}

                            <div style={{ marginTop: 8 }}>
                                {shouldRenderHtmlMessage ? (
                                    <h3
                                        style={styles.subHeading}
                                        dangerouslySetInnerHTML={{ __html: primaryMessage }}
                                    />
                                ) : (
                                    <h3 style={styles.subHeading}>{primaryMessage}</h3>
                                )}
                            </div>

                            {/* Technical details for support teams */}
                            {technicalDetails && (
                                <div style={styles.techBlock} aria-label="Technical details">
                                    {technicalDetails}
                                </div>
                            )}
                        </>
                    )}

                    <div style={styles.buttonRow}>
                        {showHideBackToLogin() && (
                            <PrimaryButton
                                text={getRuleValidationMessage(
                                    sessionResourceData,
                                    MessageTypeText.FieldLabelText,
                                    "BackToLogin",
                                    UIControlsText.BackToLogin,
                                )}
                                onClick={backToLogin}
                                title={getRuleValidationMessage(
                                    sessionResourceData,
                                    MessageTypeText.FieldLabelText,
                                    "BackToLogin",
                                    UIControlsText.BackToLogin,
                                )}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
